import React, { useState } from 'react';
import LocationAutocomplete from '@/components/location/LocationAutocomplete';

/**
 * Test page for Phase 1: Google Places API Migration
 * 
 * Validation Tests:
 * 1. Type "YMCA" - should show YMCA locations with business categorization
 * 2. Type "Atlanta" - should show city/location
 * 3. Type "Camp" - should show camps and campgrounds
 * 4. Console should show "✅ Google Maps API loaded" on page load
 * 5. Console should show "✅ Autocomplete widget initialized" when component mounts
 * 6. No deprecation warnings in console
 */
const GooglePlacesTest = () => {
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [testResults, setTestResults] = useState([]);

  const handlePlaceSelect = (place) => {
    console.log('Place selected:', place);
    setSelectedPlace(place);
    
    // Add to test results
    const result = {
      timestamp: new Date().toLocaleTimeString(),
      name: place.name || 'N/A',
      address: place.formatted_address,
      category: place.category,
      searchType: place.searchType,
      icon: place.icon,
      label: place.label,
      rating: place.rating,
      placeId: place.place_id
    };
    
    setTestResults(prev => [result, ...prev].slice(0, 5));
  };

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Google Places API Migration - Phase 1 Test
          </h1>
          <p className="text-gray-600">
            Testing modern Google Autocomplete widget with business & location search
          </p>
        </div>

        {/* Test Instructions */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
          <h2 className="text-lg font-semibold text-blue-900 mb-3">
            📋 Validation Tests
          </h2>
          <ol className="list-decimal list-inside space-y-2 text-blue-800">
            <li>Type "YMCA" - should show YMCA locations with business categorization</li>
            <li>Type "Atlanta" - should show city/location</li>
            <li>Type "Camp" - should show camps and campgrounds</li>
            <li>Open Console - should see "✅ Google Maps API loaded"</li>
            <li>Check Console - should see "✅ Autocomplete widget initialized"</li>
            <li>Verify - NO deprecation warnings in console</li>
          </ol>
        </div>

        {/* Autocomplete Widget */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Search Test
          </h2>
          <LocationAutocomplete
            onPlaceSelect={handlePlaceSelect}
            placeholder="Search camps, organizations, or locations..."
          />
        </div>

        {/* Selected Place Details */}
        {selectedPlace && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Selected Place Details
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="text-sm font-medium text-gray-500">Name:</span>
                <p className="text-gray-900">{selectedPlace.name || 'N/A'}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">Category:</span>
                <p className="text-gray-900">
                  {selectedPlace.icon} {selectedPlace.category} ({selectedPlace.searchType})
                </p>
              </div>
              <div className="col-span-2">
                <span className="text-sm font-medium text-gray-500">Address:</span>
                <p className="text-gray-900">{selectedPlace.formatted_address}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">Coordinates:</span>
                <p className="text-gray-900">
                  {selectedPlace.location?.lat?.toFixed(4)}, {selectedPlace.location?.lng?.toFixed(4)}
                </p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">Place ID:</span>
                <p className="text-gray-900 text-xs truncate">{selectedPlace.place_id}</p>
              </div>
              {selectedPlace.rating && (
                <div>
                  <span className="text-sm font-medium text-gray-500">Rating:</span>
                  <p className="text-gray-900">
                    ⭐ {selectedPlace.rating} ({selectedPlace.user_ratings_total} reviews)
                  </p>
                </div>
              )}
              {selectedPlace.business_status && (
                <div>
                  <span className="text-sm font-medium text-gray-500">Status:</span>
                  <p className="text-gray-900">{selectedPlace.business_status}</p>
                </div>
              )}
            </div>

            {/* Raw Data */}
            <details className="mt-4">
              <summary className="text-sm font-medium text-gray-500 cursor-pointer hover:text-gray-700">
                View Raw Data
              </summary>
              <pre className="mt-2 p-4 bg-gray-50 rounded text-xs overflow-auto max-h-96">
                {JSON.stringify(selectedPlace, null, 2)}
              </pre>
            </details>
          </div>
        )}

        {/* Test Results History */}
        {testResults.length > 0 && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Recent Test Results
            </h2>
            <div className="space-y-3">
              {testResults.map((result, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1">
                      <span className="text-2xl">{result.icon}</span>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-gray-900">{result.name}</p>
                          <span className="text-xs px-2 py-0.5 bg-purple-100 text-brand-700 rounded-full">
                            {result.label}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">{result.address}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          Type: {result.searchType} | {result.timestamp}
                        </p>
                      </div>
                    </div>
                    {result.rating && (
                      <div className="text-sm text-yellow-600">
                        ⭐ {result.rating}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Status Indicators */}
        <div className="mt-6 bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            API Status
          </h2>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded-full ${window.google?.maps ? 'bg-green-500' : 'bg-red-500'}`}></div>
              <span className="text-sm text-gray-700">
                Google Maps API: {window.google?.maps ? 'Loaded' : 'Not Loaded'}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded-full ${window.google?.maps?.places ? 'bg-green-500' : 'bg-red-500'}`}></div>
              <span className="text-sm text-gray-700">
                Places Library: {window.google?.maps?.places ? 'Loaded' : 'Not Loaded'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GooglePlacesTest;
